/**
 * Game Types
 * Types for game state, phases, and entities
 */

// Re-export types that are shared between frontend and backend
export interface Board {
  id: number;
  name: string;
  type: string;
  location?: {
    projectId: number;
    projectKey: string;
    projectName: string;
  };
}

export interface Sprint {
  id: number;
  name: string;
  state: 'active' | 'future' | 'closed';
  startDate?: string;
  endDate?: string;
  goal?: string;
}

export interface IssueStatus {
  id: string;
  name: string;
  statusCategory: {
    id: number;
    key: string;
    name: string;
  };
}

export interface Assignee {
  accountId: string;
  displayName: string;
  emailAddress?: string;
  avatarUrls?: {
    '16x16'?: string;
    '24x24'?: string;
    '32x32'?: string;
    '48x48'?: string;
  };
}

export interface Issue {
  id: string;
  key: string;
  fields: {
    summary: string;
    status: IssueStatus;
    assignee: Assignee | null;
    storyPoints?: number;
    issuetype?: {
      id: string;
      name: string;
      iconUrl?: string;
    };
    priority?: {
      id: string;
      name: string;
      iconUrl?: string;
    };
    labels?: string[];
    created?: string;
    updated?: string;
  };
}

export interface SquadMember {
  accountId: string;
  displayName: string;
  avatarUrl: string;
  issues: Issue[];
}

export interface Blocker {
  memberId: string;
  memberName: string;
  description: string;
  issueKey?: string;
  timestamp: Date;
}

export interface ChouquetteDebt {
  memberId: string;
  memberName: string;
  issueKey?: string;
  timestamp: Date;
}

/**
 * Game phases following the state machine
 */
export type GamePhase =
  | 'BOOT'              // Title screen + board/sprint selection
  | 'LOADING'           // Fetching Jira data
  | 'ORPHAN_CHECK'      // Handling unassigned issues
  | 'GAME_MODE_SELECT'  // Choose Slot or Pac-Man
  | 'SPIN'              // Slot machine
  | 'SPIN_RESULT'       // Result + Absent/Late options
  | 'PACMAN_MAZE'       // Pac-Man chase game
  | 'PACMAN_KANBAN'     // Pac-Man member presentation
  | 'PRESENTATION'      // Member's turn (slot mode)
  | 'RECAP';            // Daily summary

/**
 * Game mode selection
 */
export type GameMode = 'SLOT' | 'PACMAN';

/**
 * Complete game state
 */
export interface GameState {
  phase: GamePhase;
  selectedBoard: Board | null;
  selectedSprint: Sprint | null;
  members: SquadMember[];
  remainingMembers: SquadMember[];
  lateMembers: SquadMember[];      // Pass at the end
  absentMembers: SquadMember[];
  currentMember: SquadMember | null;
  orphanIssues: Issue[];
  currentOrphanIndex: number;
  blockers: Blocker[];
  chouquetteMode: boolean;
  chouquetteDebts: ChouquetteDebt[];
  dailyStartTime: Date | null;
  issuesWithoutEstimate: Issue[];
  timerStartTime: Date | null;
  timerDuration: number; // in seconds
  isTimerRunning: boolean;
  // Pac-Man mode
  gameMode: GameMode | null;
  pacmanCaptureOrder: string[]; // accountIds in capture order
}

/**
 * Game action types
 */
export type GameAction =
  | { type: 'SET_PHASE'; payload: GamePhase }
  | { type: 'SELECT_BOARD'; payload: Board }
  | { type: 'SELECT_SPRINT'; payload: Sprint }
  | { type: 'SET_MEMBERS'; payload: SquadMember[] }
  | { type: 'SET_ORPHAN_ISSUES'; payload: Issue[] }
  | { type: 'SET_UNESTIMATED_ISSUES'; payload: Issue[] }
  | { type: 'SELECT_CURRENT_MEMBER'; payload: SquadMember }
  | { type: 'MARK_MEMBER_DONE' }
  | { type: 'MARK_MEMBER_ABSENT'; payload: SquadMember }
  | { type: 'MARK_MEMBER_LATE'; payload: SquadMember }
  | { type: 'NEXT_ORPHAN' }
  | { type: 'ADD_BLOCKER'; payload: Blocker }
  | { type: 'TOGGLE_CHOUQUETTE_MODE' }
  | { type: 'ADD_CHOUQUETTE_DEBT'; payload: ChouquetteDebt }
  | { type: 'START_DAILY' }
  | { type: 'START_TIMER' }
  | { type: 'STOP_TIMER' }
  | { type: 'RESET_TIMER' }
  | { type: 'RESET_GAME' }
  // Pac-Man mode actions
  | { type: 'SET_GAME_MODE'; payload: GameMode }
  | { type: 'ADD_PACMAN_CAPTURE'; payload: string }  // accountId
  | { type: 'RESET_PACMAN_CAPTURES' };

/**
 * Konami code sequence for Mode Chouquette
 */
export const KONAMI_CODE = ['ArrowUp', 'ArrowDown', 'ArrowUp', 'ArrowDown', 'KeyQ', 'KeyB'] as const;

/**
 * Fireworks code for Pac-Man last ghost easter egg
 */
export const FIREWORKS_CODE = ['ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight'] as const;

/**
 * Timer constants
 */
export const TIMER_DURATION = 120; // 2 minutes
export const TIMER_WARNING_THRESHOLD = 10; // Warning at 10s

/**
 * Game dimensions
 */
export const GAME_WIDTH = 1920;
export const GAME_HEIGHT = 1080;

/**
 * Color palette (Mario/Zelda style)
 */
export const PALETTE = {
  RED: 0xE52521,
  BLUE: 0x049CD8,
  YELLOW: 0xFBD000,
  GREEN: 0x43B047,
  WHITE: 0xFFFFFF,
  BLACK: 0x000000,
  GRAY_DARK: 0x2C2C2C,
  GOLD: 0xFFD700,
  PURPLE: 0x9B59B6,
  ORANGE: 0xF39C12,
  SUCCESS: 0x27AE60,
  WARNING: 0xF1C40F,
  ERROR: 0xE74C3C,
} as const;
